import { scaleBand, ScaleBand } from "d3-scale";

// These numbers are from the specs here:
// https://confluence.pnnl.gov/confluence/pages/viewpage.action?pageId=227386967
const MAX_SINGLE_BAR_WIDTH = 200;
const BAR_BORDER = 50;

// Enum to indicate behavior of band
enum SizingBehavior {
    paddingExpands,
    bandShrinks,
    allShrink
}


/**
 * This function wraps d3's scaleBand to add special functionality
 * to the case where the domain's length is 1.
 * 
 * When the domain's length is 1:
 * - Ideally, the band point spans from 50px from the start to 250px
 *   and has at least 50px of space after it
 * - If there isn't enough room for that (range<300px), 
 *   the span of the bar decreases as needed, down to 50px.
 * - If the range<150px, the span and padding decrease proportionally together.
 */ 
export const wrappedBand = function wrappedBand(singleBandAtEnd=false, band?:ScaleBand<string>):ScaleBand<string> {
    //// Set up wrapping of scaleBand
    const normalBand = band ? band : scaleBand()

    //// Make some helpers
    // getter for singleDomain
    const hasSingleDomain = () => {
        return normalBand.domain().length === 1;
    }

    // Total space in band
    const getBandSize = () =>{
        const [start, end] = normalBand.range()
        return  Math.abs(end-start);
    }

    // get enum indicating how the band sizing should behave
    const getSizeBehavior = () => {
        const bandSize = getBandSize()
        if (bandSize>=MAX_SINGLE_BAR_WIDTH+BAR_BORDER*2){
            return SizingBehavior.paddingExpands
        }
        if (bandSize>=BAR_BORDER*3){
            return SizingBehavior.bandShrinks
        }
        return SizingBehavior.allShrink
    }

    //// Create wrapped function
    /**
     * Make initial newBand
     * 
     * This function takes in an element of the domain (category),
     * and returns an element of the range (start of band). Normally we just use
     * the normalBand's function for this, but if there is only one
     * item in the domain, we override to make sure we center the bands
     * according to specs.
     */
    const untypedNewBand = (x) => {
        if (hasSingleDomain()) {
            const [rangeStart,rangeEnd] = normalBand.range()
            switch(getSizeBehavior()) {
                case SizingBehavior.paddingExpands:
                    // Position band relative to start or end of range
                    return singleBandAtEnd ?
                        rangeEnd - BAR_BORDER - MAX_SINGLE_BAR_WIDTH: 
                        rangeStart + BAR_BORDER;
                case SizingBehavior.allShrink:
                    // When all shrinking, band starts at 
                    // one third of total width
                    return rangeStart + getBandSize()/3;
                case SizingBehavior.bandShrinks:
                    return rangeStart + BAR_BORDER;
            }
        }
        else return normalBand(x)
    }

    /**
     * Function wrapper that makes sure to return the wrapped
     * band if a ScaleBand is being returned
     */
    const makeReturnWrapped = (func:Function) => {
        return function (...args) {
            // @ts-ignore - no ts, the spread array is included
            const ret = func(...args)
            // If we were going to return normalBand,
            // return wrapped band instead
            if(ret===normalBand) {
                return untypedNewBand
            }
            return ret
        }
    }

    // copy all props
    for(var prop in normalBand) {
        if (normalBand.hasOwnProperty(prop)) {
            untypedNewBand[prop] = makeReturnWrapped(normalBand[prop]);
        }
    }
    // it has ScaleBand's props so we can type cast in good faith now
    const newBand = untypedNewBand as ScaleBand<string>

    //// Overrides
    /**
     * If hasSingleDomain then we need to
     * figure out the correct size for our single band
     * and override normal functionality
     */
    newBand.bandwidth = () => {
        if (hasSingleDomain()) {
            const bandSize = getBandSize()
            switch(getSizeBehavior()) {
                case SizingBehavior.paddingExpands:
                    return MAX_SINGLE_BAR_WIDTH;
                case SizingBehavior.bandShrinks:
                    return (bandSize - BAR_BORDER*2)
                case SizingBehavior.allShrink:
                    return (bandSize/3)
            }
        }
        else return normalBand.bandwidth()
    } 

    /**
     * Make sure copies are still wrapped
     */
    newBand.copy = () => {
        return wrappedBand(singleBandAtEnd,normalBand.copy())
    }

    return newBand
}
